import React, { useMemo, useState } from 'react';

import { Alert, Checkbox, Table } from 'antd';

import styled, { css } from 'styled-components';

import { capitalize } from '@libs/NwUtils';

import { useUserPermission } from '@contexts/UserContext';

import { getContactName, getContactType } from '@hooks/API/Contact/UseContactsList';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import BillingInfo from '@components/BillingInfo/BillingInfo';
import Money from '@components/Gui/Money';
import NwIcon from '@components/Gui/NwIcon';
import { NwButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import StyleVariables from '@components/Gui/StyleVariables';
import CreateInvoiceDrawer from '@components/InvoicesAndCreditNotes/CreateInvoiceDrawer';
import InvoiceDrawer from '@components/InvoicesAndCreditNotes/InvoiceDrawer';
import { adaptProjectGroupedStatements } from '@components/StatementRowsCommon/adapters';
import {
    StatementAmountCell,
    StatementActionsCell,
    StatementDateCell,
    StatementTitleCell,
    SubjectStatementStatusCell,
    StatementProjectCell,
    StatementTransactionPercentage,
} from '@components/StatementRowsCommon/cells';
import {
    buildInvoiceDraftData,
    countAgencyFeeStatements,
    groupDisplayedStatementTotalsByCurrency,
    hasMixedCurrenciesForInvoice,
} from '@components/StatementRowsCommon/helpers';
import TransactionContact from '@components/Transactions/TransactionsDrawer/TransactionDetailForms/TransactionContact';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const StatementCellWrapper = styled.div`
    ${(props) =>
        props.$isCommission &&
        css`
            margin-top: -2px;
            background-color: white;
        `}
`;

const ProjectGroupedStatementsBillingInfo = ({
    contactID,
    contactName,
    statementType,
    billingInfo,
    isSingleBillingInfo,
    onEditTransaction,
    project,
    readOnly,
    isExpanded,
    onReloadProject,
    showTotals = false,
    singleCellDetails = false,
}) => {
    const [selectedStatementIDs, setSelectedStatementIDs] = useState([]);
    const [indeterminate, setIndeterminate] = useState(false);
    const [checkAll, setCheckAll] = useState(false);
    const [showCreateInvoiceDrawer, setShowCreateInvoiceDrawer] = useState(false);
    const [showInvoiceDrawer, setShowInvoiceDrawer] = useState();
    const [showChangeContactDrawer, setShowChangeContactDrawer] = useState();
    const [currentInvoiceData, setCurrentInvoiceData] = useState();
    const [transactionsIDsForContactChange, setTransactionsIDsForContactChange] = useState([]);

    const AccessAccounting = useUserPermission('AccessAccounting');

    const configurations = useConfigurations();

    const statementsRows = useMemo(() => {
        return adaptProjectGroupedStatements(billingInfo.Transactions || []);
    }, [billingInfo.Transactions]);

    const someSelected = () => {
        return statementsRows.some((statement) => selectedStatementIDs.includes(statement.ID));
    };

    const checkDifferentCurrencies = () => {
        const selectedStatements = statementsRows.filter((statement) => selectedStatementIDs.includes(statement.ID));

        return hasMixedCurrenciesForInvoice(selectedStatements, configurations.defaultCurrency);
    };

    const prepareInvoiceData = () => {
        setShowCreateInvoiceDrawer(false);
        const selectedStatements = statementsRows.filter((statement) => selectedStatementIDs.includes(statement.ID));
        const contactType = getContactType(contactID);
        const invoiceType = capitalize(contactType.name);

        const ret = buildInvoiceDraftData({
            statements: selectedStatements,
            contactID,
            contactName: getContactName(contactID),
            contactTypeLabel: invoiceType,
            billingInfo,
            defaultCurrency: configurations.defaultCurrency,
            projectName: project.Name,
            agencyFeeCount: countAgencyFeeStatements(selectedStatements, {
                getFamily: (statement) => statement.Transaction.Family,
                matchStatement: (statement, family) =>
                    family === 'AgFee' ||
                    (family === 'JobFee' && (statement.isCommission || statement.isClientAdvance)),
            }),
            getFamily: (statement) => statement.Transaction.Family,
        });

        setCurrentInvoiceData(ret);
        setShowCreateInvoiceDrawer(true);
    };

    const isStatementBillable = (statement) => {
        return !statement.isInvoiced && statement.Status !== 'Option' && !statement.WorkDateCancelled;
    };

    const statementIDsArray = statementsRows.filter(isStatementBillable).map((s) => s.ID);
    const allStatementsNumber = statementsRows.length;

    const onSelectChange = (selectedRowKeys, selectedRows) => {
        setSelectedStatementIDs(selectedRowKeys);
        setIndeterminate(selectedRowKeys.length > 0 && selectedRowKeys.length < allStatementsNumber);
        setCheckAll(selectedRowKeys.length === allStatementsNumber);
    };

    const onCheckAllChange = (e) => {
        const selectedKeys = e.target.checked ? statementIDsArray : [];
        setSelectedStatementIDs([...selectedKeys]);
        setIndeterminate(selectedKeys.length > 0 && selectedKeys.length < allStatementsNumber);
        setCheckAll(e.target.checked);
    };

    const rowSelection = {
        selectedRowKeys: selectedStatementIDs,
        renderCell: function (checked, record, index, originNode) {
            return (
                <div className="selection-wrapper">
                    <div>
                        <SubjectStatementStatusCell
                            statement={record}
                            mode="statement-with-payment"
                            statementDirection="receivable"
                            compact
                            detailContactType={statementType?.toLowerCase()}
                            invoiceDrawerProps={{ reloadBillingProjectsSearchOnClose: false }}
                        />
                    </div>
                    <div>{originNode}</div>
                </div>
            );
        },
        onChange: onSelectChange,
        getCheckboxProps: (record) => {
            return { disabled: record.isInvoiced };
        },
    };

    const transactionColumns = [
        {
            title: 'Date',
            key: 'Date',
            dataIndex: 'Date',
            className: 'cell-border-left cell-border-right',
            render: (text, record) => (
                <StatementCellWrapper $isCommission={record.isCommission}>
                    <StatementDateCell row={record} variant="project-grouped" />
                </StatementCellWrapper>
            ),
        },
        {
            title: 'Model Status',
            dataIndex: 'Payment',
            key: 'Payment',
            className: 'cell-border-right',
            render: (text, record) => (
                <StatementCellWrapper $isCommission={record.isCommission}>
                    <SubjectStatementStatusCell
                        statement={record}
                        mode="statement-with-payment"
                        statementDirection="payable"
                        detailContactType={statementType?.toLowerCase()}
                    />
                </StatementCellWrapper>
            ),
        },
        {
            title: singleCellDetails ? 'Details' : 'Title',
            key: 'Title',
            dataIndex: 'Title',
            width: singleCellDetails ? '65%' : '30%',
            render: (text, record) => {
                if (singleCellDetails) {
                    return (
                        <StatementCellWrapper $isCommission={record.isCommission}>
                            <div className="transaction-meta-wrapper">
                                <StatementTitleCell
                                    row={record}
                                    statementType={statementType}
                                    variant="project-grouped"
                                    project={project}
                                    isExpanded={isExpanded}
                                />
                                {record.isCommission ? (
                                    <StatementTransactionPercentage row={record} />
                                ) : (
                                    <StatementProjectCell project={project} row={record} showEvent />
                                )}
                            </div>
                        </StatementCellWrapper>
                    );
                }

                return (
                    <StatementCellWrapper $isCommission={record.isCommission}>
                        <StatementTitleCell
                            row={record}
                            statementType={statementType}
                            variant="project-grouped"
                            project={project}
                            isExpanded={isExpanded}
                        />
                    </StatementCellWrapper>
                );
            },
        },
        ...(!singleCellDetails
            ? [
                  {
                      title: 'Project',
                      dataIndex: 'Project',
                      key: 'Project',
                      width: '35%',
                      className: 'top-aligned',
                      render: (text, record) => {
                          if (!record.isCommission) {
                              return (
                                  <StatementCellWrapper $isCommission={record.isCommission}>
                                      <StatementProjectCell project={project} row={record} showEvent />
                                  </StatementCellWrapper>
                              );
                          } else {
                              return (
                                  <StatementCellWrapper $isCommission={record.isCommission}>
                                      <StatementTransactionPercentage row={record} />
                                  </StatementCellWrapper>
                              );
                          }
                      },
                  },
              ]
            : []),
        {
            title: 'Amount',
            dataIndex: 'Amount',
            key: 'Amount',
            width: '40%',
            align: 'right',
            className: 'cell-border-right',
            render: (text, record) => {
                return (
                    <StatementCellWrapper $isCommission={record.isCommission}>
                        <StatementAmountCell
                            row={record}
                            defaultCurrency={configurations.defaultCurrency}
                            variant="project-grouped"
                            onEditTransaction={onEditTransaction}
                            readOnly={readOnly}
                            isExpanded={isExpanded}
                        />
                    </StatementCellWrapper>
                );
            },
        },
        {
            title: 'Actions',
            key: 'Actions',
            align: 'center',
            render: (text, record) => {
                return (
                    <StatementCellWrapper $isCommission={record.isCommission}>
                        <StatementActionsCell
                            variant="project-grouped"
                            row={record}
                            onEditTransaction={readOnly ? undefined : onEditTransaction}
                        />
                    </StatementCellWrapper>
                );
            },
        },
    ];

    if (statementType === 'Client') {
        transactionColumns.splice(1, 1);
    }

    const clearSelection = () => {
        setSelectedStatementIDs([]);
        setIndeterminate(false);
        setCheckAll(false);
    };

    const handleInvoiceCreated = (newInvoiceID) => {
        setShowInvoiceDrawer(newInvoiceID);
        setShowCreateInvoiceDrawer(false);
        clearSelection();
    };

    const handleCloseInvoiceDrawer = async () => {
        setShowInvoiceDrawer(null);
        if (onReloadProject) {
            await onReloadProject();
        }
    };

    const handleChangeSelectedTransactionContact = () => {
        const selectedTransactionsIDs = statementsRows
            .filter((st) => selectedStatementIDs.includes(st.ID))
            .map((st) => st.TransactionID);
        setTransactionsIDsForContactChange([...selectedTransactionsIDs]);
        setShowChangeContactDrawer(true);
    };

    const handleChangeTransactionContact = () => {
        setTransactionsIDsForContactChange(statementsRows.map((sr) => sr.TransactionID));
        setShowChangeContactDrawer(true);
    };

    const handleCloseTransactionContactDrawer = () => {
        setTransactionsIDsForContactChange([]);
        setShowChangeContactDrawer(false);
    };

    const isStatementConfirmed = (statement) => {
        return statement.Status !== 'Option';
    };

    const invoiceCreationAllowed = AccessAccounting && !readOnly && statementType === 'Client';
    const areSomeTransactionsInvoiced = statementsRows.some((st) => st.InvoiceID);
    const showBillingContactLink = statementType !== 'Agency';
    const titleColumnIndex = transactionColumns.findIndex((column) => column.key === 'Title');
    const amountColumnIndex = transactionColumns.findIndex((column) => column.key === 'Amount');
    const leadingSummaryColSpan = titleColumnIndex + (invoiceCreationAllowed ? 1 : 0);
    const betweenTitleAndAmountColSpan = Math.max(amountColumnIndex - titleColumnIndex - 1, 0);
    const trailingSummaryColSpan = Math.max(transactionColumns.length - amountColumnIndex - 1, 0);
    const summaryCellStyle = {
        padding: 0,
        backgroundColor: 'white',
        borderTop: `2px solid ${StyleVariables.PrimaryColor}`,
    };

    return (
        <div className="billinginfo-line" key={`billinginfo-${billingInfo.ID}`}>
            <div className="billinginfo-header">
                {!readOnly && statementType === 'Client' && (
                    <div className="billinginfo-header-left">
                        <Checkbox indeterminate={indeterminate} onChange={onCheckAllChange} checked={checkAll} />
                    </div>
                )}
                {isSingleBillingInfo && contactName ? (
                    <>
                        <div className="billinginfo-header-middle">
                            <h2>{contactName}</h2>
                        </div>
                        <div className="billinginfo-header-right">
                            <BillingInfo
                                data={billingInfo}
                                inline
                                contactName={contactName}
                                showChangeBillingContactLink={showBillingContactLink}
                                disableChangeBillingContactLink={areSomeTransactionsInvoiced}
                                onChangeBillingContact={handleChangeTransactionContact}
                            />
                        </div>
                    </>
                ) : (
                    <div className="billinginfo-header-right">
                        <BillingInfo
                            data={billingInfo}
                            inline
                            contactName={contactName}
                            showChangeBillingContactLink={showBillingContactLink}
                            disableChangeBillingContactLink={areSomeTransactionsInvoiced}
                            onChangeBillingContact={handleChangeTransactionContact}
                        />
                    </div>
                )}
            </div>
            <div className={`transactions-container transactions-container-statements${readOnly ? ' read-only' : ''}`}>
                <Table
                    rowSelection={invoiceCreationAllowed ? rowSelection : null}
                    showHeader={false}
                    pagination={false}
                    columns={transactionColumns}
                    size="small"
                    dataSource={statementsRows}
                    rowKey="ID"
                    rowClassName={(record) => {
                        let cn = '';
                        if (record.Transaction.WorkDateCancelled) {
                            cn += 'cancelled';
                        }
                        if (!isStatementConfirmed(record)) {
                            cn += ' not-confirmed';
                        }
                        return cn;
                    }}
                    summary={
                        showTotals
                            ? (pageData) => {
                                  const statementTotals = groupDisplayedStatementTotalsByCurrency(
                                      pageData,
                                      configurations.defaultCurrency
                                  );

                                  if (statementTotals.length === 0) {
                                      return null;
                                  }

                                      return (
                                          <Table.Summary.Row>
                                              <Table.Summary.Cell
                                                  index={0}
                                                  style={summaryCellStyle}
                                              >
                                                  <div className="summary-icon-wrapper">
                                                      <NwIcon icon={byPrefixAndName.fass['sigma']} />
                                                  </div>
                                              </Table.Summary.Cell>
                                              {leadingSummaryColSpan > 1 && (
                                                  <Table.Summary.Cell
                                                      index={1}
                                                      colSpan={leadingSummaryColSpan - 1}
                                                      style={summaryCellStyle}
                                                  />
                                              )}
                                              <Table.Summary.Cell
                                                  index={leadingSummaryColSpan}
                                                  style={summaryCellStyle}
                                              >
                                                  <div style={{ textTransform: 'uppercase', padding: '0.5rem 0.75rem' }}>
                                                      <small>totals</small>
                                                  </div>
                                              </Table.Summary.Cell>
                                              {betweenTitleAndAmountColSpan > 0 && (
                                                  <Table.Summary.Cell
                                                      index={leadingSummaryColSpan + 1}
                                                      colSpan={betweenTitleAndAmountColSpan}
                                                      style={summaryCellStyle}
                                                  />
                                              )}
                                              <Table.Summary.Cell
                                                  index={amountColumnIndex + (invoiceCreationAllowed ? 1 : 0)}
                                                  style={summaryCellStyle}
                                              >
                                                  <div style={{ padding: '0.5rem 0.75rem', lineHeight: '1.3em' }}>
                                                      <div>
                                                          {statementTotals.map((item) => (
                                                          <Money
                                                              key={item.Currency}
                                                              dineroObj={item.Dinero}
                                                              redIfNegative
                                                              boldIfDefaultCurrency
                                                              style={{ display: 'block', textAlign: 'right' }}
                                                          />
                                                      ))}
                                                  </div>
                                                  </div>
                                              </Table.Summary.Cell>
                                              {trailingSummaryColSpan > 0 && (
                                                  <Table.Summary.Cell
                                                      index={amountColumnIndex + (invoiceCreationAllowed ? 2 : 1)}
                                                      colSpan={trailingSummaryColSpan}
                                                      style={summaryCellStyle}
                                                  />
                                              )}
                                          </Table.Summary.Row>
                                      );
                              }
                            : undefined
                    }
                />
            </div>
            <div className="actions-on-selected-lines">
                {selectedStatementIDs.length > 0 && (
                    <small className="selected-lines-counter">{selectedStatementIDs.length} selected items</small>
                )}
                {invoiceCreationAllowed && someSelected(billingInfo) && (
                    <div className="actions-on-selected-lines-content">
                        {checkDifferentCurrencies() ? (
                            <Alert
                                message="You can't create an invoice with different currencies"
                                type="error"
                                showIcon
                                style={{ marginTop: '.5rem', marginBottom: '.5rem' }}
                            />
                        ) : (
                            <NwButton
                                primary
                                size="small"
                                label="create invoice"
                                onClick={() => {
                                    prepareInvoiceData();
                                }}
                                disabled={checkDifferentCurrencies()}
                            />
                        )}
                        {statementType !== 'Agency' && (
                            <NwButton
                                primary
                                size="small"
                                label="change billing contact"
                                onClick={() => {
                                    handleChangeSelectedTransactionContact();
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
            {showCreateInvoiceDrawer && (
                <CreateInvoiceDrawer
                    onClose={() => setShowCreateInvoiceDrawer(false)}
                    onInvoiceCreated={handleInvoiceCreated}
                    data={currentInvoiceData}
                    projectCustomFields={project.CustomFields}
                />
            )}
            {showInvoiceDrawer && <InvoiceDrawer invoiceId={showInvoiceDrawer} onClose={handleCloseInvoiceDrawer} />}
            {showChangeContactDrawer && (
                <NwDrawer title={`Change Transaction ${statementType}`} onClose={handleCloseTransactionContactDrawer}>
                    <TransactionContact
                        contactID={contactID}
                        contactType={statementType}
                        billingInfoID={billingInfo.ID}
                        transactionIDs={transactionsIDsForContactChange}
                        transactionFamily="jobfee"
                        onChangeTransactionContact={handleCloseTransactionContactDrawer}
                        projectID={project.ID}
                    />
                </NwDrawer>
            )}
        </div>
    );
};

export default ProjectGroupedStatementsBillingInfo;
